<template>
  <div class="carousel-container" ref="carouselRef">
    <!-- Botão para navegar para o slide anterior -->
    <button 
      @click="prevSlide" 
      class="carousel-control prev-links" 
      :disabled="currentIndex === 0"
      :class="{ disabled: currentIndex === 0 }"
      aria-label="Previous slide"
    >
      <img class="setas-navegacao" src="@/assets/icons/seta-left.svg" alt="Seta para a esquerda" />
    </button>

    <div class="carousel">
      <div class="carousel-track" :style="{ transform: translateValue }">
        <div v-for="(group, groupIndex) in groupedButtons" :key="groupIndex" class="carousel-group">
          <div v-for="(button, index) in group" :key="index" 
               class="carousel-slide">
            <a :href="button.route" class="carousel-button" @click.prevent="button.action">
              <img :src="button.icon" :alt="button.alt" class="button-icon" />
              <span class="text-button">{{ button.text }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Botão para navegar para o próximo slide -->
    <button 
      @click="nextSlide" 
      class="carousel-control next-links" 
      :disabled="isAtEnd"
      :class="{ disabled: isAtEnd }"
      aria-label="Next slide"
    >
      <img class="setas-navegacao" src="@/assets/icons/seta-right.svg" alt="Seta para a direita" />
    </button>
  </div>
</template>

<script>
export default {
  name: 'Carousel',
  props: {
    buttons: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentIndex: 0,
      slideWidth: 0,
      slidesPerView: 5,
      groupedSlides: []
    };
  },
  computed: {
    // Agrupa os slides em conjuntos baseados no slidesPerView
    groupedButtons() {
      const groups = [];
      for (let i = 0; i < this.buttons.length; i += this.slidesPerView) {
        groups.push(this.buttons.slice(i, i + this.slidesPerView));
      }
      return groups;
    },
    isAtEnd() {
      return this.currentIndex >= this.groupedButtons.length - 1;
    },
    translateValue() {
      return `translateX(-${this.currentIndex * 100}%)`;
    }
  },
  methods: {
    calculateSlideWidth() {
      if (this.$refs.carouselRef) {
        const containerWidth = this.$refs.carouselRef.offsetWidth - 120;
        const totalGaps = (this.slidesPerView - 1) * 12;
        this.slideWidth = Math.floor((containerWidth - totalGaps) / this.slidesPerView);
      }
    },
    updateSlidesPerView() {
      const width = window.innerWidth;
      if (width <= 640) {
        this.slidesPerView = 1;
      } else if (width <= 900) {
        this.slidesPerView = 2;
      } else if (width <= 1100) {
        this.slidesPerView = 3;
      } else {
        this.slidesPerView = 5;
      }
      
      this.calculateSlideWidth();
      this.adjustCurrentIndex();
    },
    adjustCurrentIndex() {
      const maxIndex = Math.max(0, Math.ceil(this.buttons.length / this.slidesPerView) - 1);
      this.currentIndex = Math.min(this.currentIndex, maxIndex);
    },
    nextSlide() {
      if (!this.isAtEnd) {
        this.currentIndex++;
      }
    },
    prevSlide() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      }
    }
  },
  mounted() {
    this.updateSlidesPerView();
    window.addEventListener('resize', this.updateSlidesPerView);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateSlidesPerView);
  }
};
</script>

<style>
@import '../../assets/css/variables.css';

.carousel-container {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  gap: 12px !important;
  margin: 0 auto !important;
  box-sizing: border-box !important;
  position: relative !important;
}

.carousel-control {
  position: static !important;
  transform: none !important;
  flex-shrink: 0 !important;
  width: 44px !important;
  height: 44px !important;
  background-color: #ffffff !important;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 50% !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  margin: 0 !important;
  transition: all 0.25s ease !important;
  z-index: 2 !important;
}

.carousel-control:hover:not(.disabled) {
  background-color: #f0f4ff !important;
  border-color: var(--primary-color, #193155) !important;
  transform: translateY(-2px) !important;
  box-shadow: 0 4px 12px rgba(25, 49, 85, 0.12) !important;
}

.carousel-control.disabled {
  opacity: 0.35 !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
  box-shadow: none !important;
  transform: none !important;
}

.setas-navegacao {
  width: 20px !important;
  height: 20px !important;
  object-fit: contain !important;
  opacity: 0.75 !important;
  transition: opacity 0.25s ease !important;
}

.carousel-control:hover:not(.disabled) .setas-navegacao {
  opacity: 1 !important;
}

.carousel {
  flex: 1 !important;
  width: auto !important;
  max-width: 100% !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-start !important;
  margin: 0 !important;
  height: auto !important;
}

.carousel-track {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
  width: 100% !important;
  padding: 6px 0 !important;
  margin: 0 !important;
}

.carousel-group {
  display: flex !important;
  flex-direction: row !important;
  flex: 0 0 100% !important;
  justify-content: flex-start !important;
  align-items: center !important;
  gap: 12px !important;
}

.carousel-slide {
  flex: 0 0 calc((100% - 4 * 12px) / 5) !important;
  width: calc((100% - 4 * 12px) / 5) !important;
  max-width: calc((100% - 4 * 12px) / 5) !important;
  min-width: 0 !important;
  padding: 0 !important;
  box-sizing: border-box !important;
  display: flex !important;
  justify-content: center !important;
}

.carousel-button {
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  height: 74px !important;
  padding: 10px 14px !important;
  background-color: #ffffff !important;
  color: #333333 !important;
  text-decoration: none !important;
  border-radius: 14px !important;
  border: 1px solid #e8ecf1 !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04) !important;
  gap: 12px !important;
  box-sizing: border-box !important;
  transition: all 0.25s ease !important;
}

.carousel-button:hover {
  border-color: var(--terciary-color, #193155) !important;
  box-shadow: 0 4px 16px rgba(25, 49, 85, 0.12) !important;
  transform: translateY(-2px) !important;
  background-color: #f8faff !important;
  color: var(--primary-color, #193155) !important;
}

.button-icon {
  width: 32px !important;
  height: 32px !important;
  flex-shrink: 0 !important;
  opacity: 0.8 !important;
  margin: 0 !important;
}

.text-button {
  text-align: left !important;
  font-size: 13px !important;
  font-family: var(--primary-font) !important;
  font-weight: 600 !important;
  color: #333333 !important;
  line-height: 1.3 !important;
  overflow: hidden !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 2 !important;
  -webkit-box-orient: vertical !important;
}

.carousel-button:hover .text-button {
  color: var(--primary-color, #193155) !important;
}

@media (max-width: 1100px) {
  .carousel-slide {
    flex: 0 0 calc((100% - 2 * 12px) / 3) !important;
    width: calc((100% - 2 * 12px) / 3) !important;
    max-width: calc((100% - 2 * 12px) / 3) !important;
  }
}

@media (max-width: 900px) {
  .carousel-slide {
    flex: 0 0 calc((100% - 12px) / 2) !important;
    width: calc((100% - 12px) / 2) !important;
    max-width: calc((100% - 12px) / 2) !important;
  }
}

@media (max-width: 768px) {
  .carousel-control {
    width: 38px !important;
    height: 38px !important;
  }

  .setas-navegacao {
    width: 16px !important;
    height: 16px !important;
  }

  .carousel-container {
    gap: 8px !important;
  }
}

@media (max-width: 640px) {
  .carousel-slide {
    flex: 0 0 100% !important;
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>